import { Injectable } from '@angular/core';

export interface YouTubeVideo {
  videoId: string;
  title: string;
  channelName: string;
  thumbnailUrl: string;
  youtubeUrl: string;
}

// Set your YouTube Data API v3 key here.
// Enable "YouTube Data API v3" at console.cloud.google.com → APIs & Services.
const YOUTUBE_API_KEY = 'AIzaSyC0zVYyGvN6FwbmYywhMfqlfPXBb5DwqXE';

@Injectable({ providedIn: 'root' })
export class YouTubeImportService {

  /** Extract the list= playlist ID from any YouTube URL format. */
  extractPlaylistId(url: string): string | null {
    try {
      const u = new URL(url.trim());
      const list = u.searchParams.get('list');
      return list && list.startsWith('PL') || list?.startsWith('UU') || list?.startsWith('FL') || list?.startsWith('RD')
        ? list
        : (list ?? null);
    } catch {
      // Try regex fallback for URLs without a protocol
      const match = url.match(/[?&]list=([A-Za-z0-9_-]+)/);
      return match ? match[1] : null;
    }
  }

  isValidPlaylistUrl(url: string): boolean {
    return !!this.extractPlaylistId(url.trim());
  }

  isConfigured(): boolean {
    return !!YOUTUBE_API_KEY;
  }

  /** Fetch all videos from a YouTube playlist (handles pagination, caps at 200). */
  async fetchPlaylistVideos(playlistId: string): Promise<YouTubeVideo[]> {
    if (!YOUTUBE_API_KEY) throw new Error('YouTube API key not configured.');

    const videos: YouTubeVideo[] = [];
    let nextPageToken: string | undefined;
    const MAX_SONGS = 200;

    do {
      const params = new URLSearchParams({
        part: 'snippet',
        playlistId,
        maxResults: '50',
        key: YOUTUBE_API_KEY,
        ...(nextPageToken ? { pageToken: nextPageToken } : {})
      });

      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/playlistItems?${params}`
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const msg = (err as any)?.error?.message ?? `HTTP ${response.status}`;
        throw new Error(msg);
      }

      const data: any = await response.json();

      for (const item of data.items ?? []) {
        const snippet = item.snippet;
        const videoId: string = snippet?.resourceId?.videoId;
        if (!videoId) continue;
        const title: string = snippet.title ?? '';
        if (title === 'Deleted video' || title === 'Private video') continue;

        videos.push({
          videoId,
          title,
          channelName: snippet.videoOwnerChannelTitle ?? '',
          thumbnailUrl:
            snippet.thumbnails?.medium?.url ??
            snippet.thumbnails?.default?.url ??
            `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
          youtubeUrl: `https://www.youtube.com/watch?v=${videoId}`
        });

        if (videos.length >= MAX_SONGS) return videos;
      }

      nextPageToken = data.nextPageToken;
    } while (nextPageToken);

    return videos;
  }
}
